import { useState, useCallback } from "react";
import { format } from "date-fns";
import {
  MapPin,
  FileText,
  Pencil,
  Trash2,
  Undo2,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  Files,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import type { ExpenseItem } from "../hooks/useProviderExpenses";
import {
  formatExpenseCategory,
  formatCurrency,
  parseExpenseDate,
  ExpenseStatus,
} from "../utils/expense-schema";
import { getReceiptUrl, openReceiptUrl } from "../utils/receipt-utils";

interface ExpenseCardProps {
  expense: ExpenseItem;
  onEdit?: (expense: ExpenseItem) => void;
  onDelete?: (expenseId: string) => void;
  isDeleting?: boolean;
}

export function ExpenseCard({
  expense,
  onEdit,
  onDelete,
  isDeleting,
}: ExpenseCardProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loadingReceiptIndex, setLoadingReceiptIndex] = useState<number | null>(
    null,
  );

  const isPending = expense.status === ExpenseStatus.PendingOrgReview;
  const isApproved = expense.status === ExpenseStatus.Approved;
  const isRejected = expense.status === ExpenseStatus.Rejected;

  const canEdit = isRejected;
  const canDelete = isPending || isRejected;

  const handleViewReceipt = useCallback(
    async (receiptId: string, index: number) => {
      setLoadingReceiptIndex(index);
      try {
        const url = await getReceiptUrl(receiptId);
        await openReceiptUrl(url);
      } catch (error) {
        console.error("Failed to load receipt:", error);
      } finally {
        setLoadingReceiptIndex(null);
      }
    },
    [],
  );

  return (
    <Card
      className={cn(
        "overflow-hidden",
        isPending && "border-amber-200 bg-amber-50/30",
        isApproved && "border-green-200 bg-green-50/30",
        isRejected && "border-red-200 bg-red-50/30",
      )}
    >
      <CardHeader className="pb-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Date and category */}
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-lg font-bold text-slate-900">
                {format(parseExpenseDate(expense.expenseDate), "MMM d, yyyy")}
              </span>
              <Badge variant="outline" className="text-xs">
                {formatExpenseCategory(expense.category)}
              </Badge>
              {isPending && (
                <Badge
                  variant="outline"
                  className="text-xs bg-amber-100 text-amber-700 border-amber-200"
                >
                  <Clock className="h-3 w-3 mr-1" />
                  Pending
                </Badge>
              )}
              {isApproved && (
                <Badge
                  variant="outline"
                  className="text-xs bg-green-100 text-green-700 border-green-200"
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Approved
                </Badge>
              )}
              {isRejected && (
                <Badge
                  variant="outline"
                  className="text-xs bg-red-100 text-red-700 border-red-200"
                >
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Rejected
                </Badge>
              )}
            </div>

            {/* Facility info */}
            <div className="flex items-start gap-1.5 text-sm text-slate-600">
              <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1.5">
                <span className="truncate">{expense.facilityName}</span>
                {expense.facilityCity && expense.facilityState && (
                  <span className="text-slate-400">
                    <span className="hidden sm:inline">• </span>
                    {expense.facilityCity}, {expense.facilityState}
                  </span>
                )}
              </div>
            </div>

            {/* Job offer title if present */}
            {expense.jobOfferTitle && (
              <div className="text-sm text-slate-500 mt-0.5 truncate">
                {expense.jobOfferTitle}
              </div>
            )}
          </div>

          {/* Amount */}
          <div className="text-right shrink-0">
            <div className="text-2xl font-bold text-slate-900">
              {formatCurrency(expense.amount)}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        {/* Receipts list */}
        <div className="mb-3">
          <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
            <Files className="h-4 w-4 text-slate-400" />
            <span>
              {expense.receipts.length} receipt
              {expense.receipts.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="space-y-1">
            {expense.receipts.map((receipt, index) => (
              <div
                key={`${receipt.path}-${index}`}
                className="flex items-center justify-between p-2 bg-slate-50 rounded-md"
              >
                <div className="flex items-center gap-2 text-sm text-slate-600 min-w-0">
                  <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                  <span className="truncate">{receipt.fileName}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleViewReceipt(receipt.id, index)}
                  disabled={loadingReceiptIndex === index}
                  className="shrink-0"
                >
                  {loadingReceiptIndex === index ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4 mr-1" />
                  )}
                  View
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Provider note if any */}
        {expense.providerNote && (
          <div className="mb-3 p-3 bg-slate-50 rounded-md">
            <div className="text-xs text-slate-500 mb-1">Note</div>
            <div className="text-sm text-slate-700">{expense.providerNote}</div>
          </div>
        )}

        {/* Rejection info if rejected */}
        {isRejected && expense.latestRejection && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-red-600 font-medium mb-1">
                  Rejected on{" "}
                  {format(
                    new Date(expense.latestRejection.rejectedAt),
                    "MMM d, yyyy",
                  )}
                </div>
                {expense.latestRejection.rejectionNote && (
                  <div className="text-sm text-red-700">
                    {expense.latestRejection.rejectionNote}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        {(canEdit || canDelete) && (
          <div className="flex items-center gap-2 pt-3 border-t">
            {canEdit && onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(expense)}
                className="flex-1"
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit & Resubmit
              </Button>
            )}
            {/* Withdraw button for pending expenses */}
            {isPending && onDelete && (
              <AlertDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
              >
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isDeleting}
                    className="flex-1 text-amber-700 border-amber-300 hover:bg-amber-50 hover:text-amber-800"
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Undo2 className="h-4 w-4 mr-1" />
                    )}
                    Withdraw
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Withdraw Expense?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will withdraw your{" "}
                      <span className="font-medium text-slate-700">
                        {formatCurrency(expense.amount)}{" "}
                        {formatExpenseCategory(expense.category).toLowerCase()}
                      </span>{" "}
                      expense submission. You can resubmit it later if needed.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        onDelete(expense.expenseId);
                        setDeleteDialogOpen(false);
                      }}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Withdraw Expense
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {/* Delete button for rejected expenses */}
            {isRejected && onDelete && (
              <AlertDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
              >
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isDeleting}
                    className={cn(
                      "text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700",
                      !canEdit && "flex-1",
                    )}
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-1" />
                    )}
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Expense?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this{" "}
                      <span className="font-medium text-slate-700">
                        {formatCurrency(expense.amount)}{" "}
                        {formatExpenseCategory(expense.category).toLowerCase()}
                      </span>{" "}
                      expense. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        onDelete(expense.expenseId);
                        setDeleteDialogOpen(false);
                      }}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Delete Expense
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
